import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, IndustryHero, IndustryIntro, UseCasesGrid,
  IndustryStats, IndustryDeliverables, IndustryCTA, Footer,
} from '../shared/IndustryPageSections';

const COLOR = '#E67E22';

const USE_CASES = [
  { icon:'bar', title:'Energy Monitoring & Analytics', to:'/industries/utilities-energy/energy-monitoring-analytics', desc:'Real-time dashboards for consumption, demand, and efficiency trends across every site and meter.' },
  { icon:'zap', title:'Battery & Energy Storage Monitoring', to:'/industries/utilities-energy/battery-energy-storage-monitoring', desc:'Track charge cycles, health, and performance of battery banks and storage systems remotely.' },
  { icon:'automation', title:'DG Automation & Remote Control', to:'/industries/utilities-energy/dg-automation-remote-control', desc:'Remote start/stop, fuel monitoring, and automated failover control for diesel generator sets.' },
  { icon:'sun', title:'Renewable Energy & Solar Monitoring', to:'/industries/utilities-energy/renewable-energy-solar-monitoring', desc:'Monitor solar panel output, inverter health, and generation efficiency across distributed sites.' },
  { icon:'plug', title:'Industrial Electrical Asset Monitoring', to:'/industries/utilities-energy/industrial-electrical-asset-monitoring', desc:'Continuous monitoring of transformers, switchgear, and electrical assets to catch faults early.' },
  { icon:'tool', title:'Predictive Maintenance & Industrial IoT', to:'/industries/utilities-energy/predictive-maintenance-industrial-iot', desc:'Sensor-driven insights that flag equipment wear before it causes downtime or failure.' },
];

const STATS = [
  { val:'25%', label:'Average energy savings with IoT monitoring' },
  { val:'60%', label:'Reduction in manual meter reading cost' },
  { val:'3×', label:'Faster fault detection vs traditional methods' },
];

const DELIVERABLES = [
  'Smart meter hardware design and firmware',
  'Multi-site remote monitoring platform',
  'Real-time alerts for faults, outages, and anomalies',
  'Energy consumption analytics and reporting',
  'Grid integration and SCADA connectivity',
  'Mobile app for field technicians',
  'Tamper detection and revenue protection features',
  'Regulatory compliance reporting and data export',
];

const INTRO_BODY = [
  "Energy wastage, undetected faults, and manual meter reading are costing India's energy and utility companies crores every year. IoT changes that equation — smart meters, real-time sensors, and cloud analytics platforms give utility operators complete visibility over their network, allowing them to act on data instead of guesswork.",
  'Zone-X builds end-to-end IoT systems for utilities, energy companies, and large industrial energy consumers. From smart meter hardware to multi-site cloud platforms, we deliver systems that reduce operational costs, improve reliability, and give your team a single view of the entire network — in real time, from anywhere.',
];

export default function UtilitiesEnergyPage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>IoT Solutions for Energy and Utilities India | Smart Metering & Grid Monitoring — Zone-X</title>
        <meta name="description" content="Zone-X builds IoT systems for utilities and energy companies in India — smart metering, solar monitoring, fault detection, water management, and energy analytics platforms." />
        <link rel="canonical" href="https://zone-x.dev/industries/utilities-energy" />
      </Helmet>

      <div ref={heroRef}>
        <IndustryHero
          tag="Industry / Utilities & Energy"
          breadcrumb="Utilities & Energy"
          title="Monitor Every Watt."
          titleAccent="Waste Nothing."
          subtitle="Zone-X builds IoT systems for energy and utility companies to track consumption, detect faults, and automate distribution — in real time."
          color={COLOR}
          visible={heroVisible}
          visual="/visuals/industries/Industry 4.png"
        />
      </div>

      <IndustryIntro heading="How IoT is Transforming the Energy & Utilities Sector" body={INTRO_BODY} color={COLOR} />
      <UseCasesGrid cases={USE_CASES} color={COLOR} />
      <IndustryStats stats={STATS} color={COLOR} />
      <IndustryDeliverables items={DELIVERABLES} color={COLOR} />
      <IndustryCTA />
      <Footer />
    </>
  );
}
